/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import { useEffect, useMemo, useState } from "react";
import { Auth } from "aws-amplify";
import { updateTeamsUserStatus } from "../services/TeamsService";
import { getConfigValues } from "../../../sharedServices/configService";
import { getUserByEmail } from "../services/User";
import { TEAMS_STATUS_UPDATE_ACTION_TYPES } from "../../../utility/constants";
import { isEmptyObject } from "../../../utility/helpers";

const useTeamsSync = (agentStats, ccpTeamsSyncEnabled) => {
	const [teamsStatus, setTeamsStatus] = useState(undefined); // true - busy, false - available

	const [isTeamsSyncEnabled, setTeamsSyncEnabled] = useState(false);
	const [teamsSyncConfig, setTeamsSyncConfig] = useState({});
	const [paceUser, setPaceUser] = useState({});
	const isCcpTeamsSyncEnabled = useMemo(() => ccpTeamsSyncEnabled, [ccpTeamsSyncEnabled]);

	useEffect(() => {
		const fetchConfigs = async () => {
			const config = await getConfigValues(["TEAMS_USER_STATUS_SYNC_STATE", "TeamsSyncConfiguration"]);
			const cognitoUser = await Auth.currentUserInfo();
			const userConfig = await getUserByEmail(cognitoUser.attributes.email);
			setPaceUser(userConfig);
			setTeamsSyncEnabled(config["TEAMS_USER_STATUS_SYNC_STATE"] === "ENABLED");
			setTeamsSyncConfig(config["TeamsSyncConfiguration"]);
		};
		fetchConfigs();
	}, []);

	const checkChatTaskMaxCapacity = (configuredCapacity, currentCapacity, OFFLINE_CONFIG) => {
		const capacity =
			(configuredCapacity.TASK !== 0 && configuredCapacity.TASK === currentCapacity.TASK) ||
			(configuredCapacity.CHAT !== 0 && configuredCapacity.CHAT === currentCapacity.CHAT);

		return OFFLINE_CONFIG.chatTaskAtMax && capacity;
	};

	const checkVoiceCurrentCapacity = (currentContacts, OFFLINE_CONFIG) => {
		const VoiceIncoming = currentContacts.find((contact) => contact.type === "VOICE" && contact.state === "connecting");
		const VoiceOnContact = currentContacts.find((contact) => contact.type === "VOICE" && contact.state === "connected");
		const VoiceACW = currentContacts.find((contact) => contact.type === "VOICE" && contact.state === "ended");

		const isVoiceAtMaxCapacity =
			(OFFLINE_CONFIG.voiceIncoming && !!VoiceIncoming) ||
			(OFFLINE_CONFIG.voiceOnContact && !!VoiceOnContact) ||
			(OFFLINE_CONFIG.voiceOnAcw && !!VoiceACW);

		return isVoiceAtMaxCapacity;
	};

	useEffect(() => {
		const config = teamsSyncConfig?.connectStatuses;
		const connectToTeamsSyncEnabled = teamsSyncConfig?.connectToTeamsSyncEnabled;
		if (
			!config ||
			isEmptyObject(paceUser) ||
			!paceUser?.azureId ||
			!paceUser?.isTeamsStatusSyncEnabled ||
			!connectToTeamsSyncEnabled ||
			!isTeamsSyncEnabled
		) {
			return;
		}
		const { configuredCapacity, currentContacts } = agentStats;
		const { azureId } = paceUser;
		const OFFLINE_CONFIG = {
			voiceIncoming: config.includes("VoiceIncoming"),
			voiceOnContact: config.includes("VoiceOnContact"),
			voiceOnAcw: config.includes("VoiceACW"),
			chatTaskAtMax: config.includes("ChatTaskMaxCapacity"),
		};
		const isChatTaskAtMaxCapacity = checkChatTaskMaxCapacity(
			configuredCapacity,
			agentStats.currentCapacity,
			OFFLINE_CONFIG,
		);
		const isVoiceAtMaxCapacity = checkVoiceCurrentCapacity(currentContacts, OFFLINE_CONFIG);
		const derivedOfflineStatus = isChatTaskAtMaxCapacity || isVoiceAtMaxCapacity;

		if (teamsStatus !== derivedOfflineStatus) {
			setTeamsStatus(derivedOfflineStatus);
			if (!derivedOfflineStatus || !isCcpTeamsSyncEnabled) {
				updateTeamsUserStatus(TEAMS_STATUS_UPDATE_ACTION_TYPES.CLEAR_PRESENCE, azureId);
			} else {
				updateTeamsUserStatus(TEAMS_STATUS_UPDATE_ACTION_TYPES.SET_PRESENCE, azureId);
			}
		}
	}, [teamsSyncConfig, agentStats, setTeamsStatus, teamsStatus, isTeamsSyncEnabled, paceUser, isCcpTeamsSyncEnabled]);
};

export default useTeamsSync;
